import React, { useCallback, useEffect, useState } from "react";
import { VerseSettings } from "./VerseSettings";
import PropTypes from "prop-types";
import {
  AllowFlyingEnabledSwitch,
  CreateAndMoveObjectsEnabledSwitch,
  CreateCamerasEnabledSwitch,
  CreateDrawingsEnabledSwitch,
  CreateEmojisEnabledSwitch,
  Is360TourEnabledSwitch,
  PinObjectsEnabledSwitch,
  TextChatEnabledSwitch,
  TourPortalsEnabledSwitch,
  VoiceChatEnabledSwitch
} from "./RoomSettingsSidebarSwitches";
import {
  InfoLabel,
  Input,
  Label,
  makeStyles,
  mergeClasses,
  Radio,
  RadioGroup,
  useId
} from "@fluentui/react-components";
import { FormattedMessage, useIntl } from "react-intl";
import configs from "../../utils/configs";
import { InviteLinkInputField } from "./InviteLinkInputField";
import { useInviteUrl } from "./useInviteUrl";
import NumericInput from "../fluent/NumericInput";
import { DIALOG_MEDIA_QUALITY_CHANGED, normalizeDialogMediaQualityState } from "../../utils/dialog-media-quality";
import {
  nextRoomParticipantLimitSettings,
  nextRoomResourceLimitSettings,
  roomParticipantLimitMax,
  roomResourceSettingsAvailability,
  roomResourceSettingMax,
  roomResourceSettingValue
} from "../../utils/room-resource-settings-utils";
import configManager from "../../singleton/ConfigManager";

const useClasses = makeStyles({
  input: {
    minWidth: "200px"
  },
  childInput: {
    marginLeft: "5px"
  },
  hidden: {
    display: "none"
  },
  wrapRow: {
    flexWrap: "wrap"
  },
  mediaQualityRow: {
    flexWrap: "wrap",
    "& > *": {
      "@media (min-width: 768px)": {
        maxWidth: "none"
      }
    }
  },
  mediaQualityInputs: {
    flexWrap: "wrap"
  },
  numberInput: {
    minWidth: "200px",
    "& input": {
      appearance: "textfield", // default behavior for number inputs
      // Apply styles for removing number input arrows in Webkit-based browsers
      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
        appearance: "none",
        margin: 0
      },
      // For Firefox (removes spinner arrows)
      '&[type="number"]': {
        "-moz-appearance": "textfield"
      }
    }
  }
});

function roomSessionMediaQualityState(settings, roomSessionPreview) {
  const roomSession = roomSessionPreview || APP.rtcSfuRoomSession;
  const roomId = settings?.hub_id || settings?.id;

  if (!roomSession?.roomMediaQuality) {
    return null;
  }

  if (roomId && roomSession.roomId !== roomId && roomSession.hubId !== roomId) {
    return null;
  }

  return roomSession.roomMediaQuality;
}

function withPersistedMediaQualityProfile(mediaQualityState, settings) {
  const profile = settings?.user_data?.media_quality_profile;

  if (!profile) {
    return mediaQualityState;
  }

  const state = typeof mediaQualityState === "object" && mediaQualityState !== null ? mediaQualityState : {};

  return {
    ...state,
    profile
  };
}

function currentDialogMediaQualityState(settings, roomSessionPreview) {
  const roomSessionMediaQuality = roomSessionMediaQualityState(settings, roomSessionPreview);
  const currentMediaQuality = APP.dialog?.roomMediaQuality || roomSessionMediaQuality;
  const capabilityMediaQuality = roomSessionMediaQuality || currentMediaQuality;

  return normalizeDialogMediaQualityState(
    withPersistedMediaQualityProfile(currentMediaQuality, settings),
    capabilityMediaQuality
  );
}

export function VerseSettingsContainer({ styles, hubChannel, settings, setSettings, roomSession }) {
  const { fetchingInvite, inviteUrl, revokeInvite } = useInviteUrl(hubChannel);
  const roomSessionPreview = roomSession || APP.rtcSfuRoomSession;
  const [roomMediaQualityState, setRoomMediaQualityState] = useState(() =>
    currentDialogMediaQualityState(settings, roomSessionPreview)
  );
  const settingsRoomId = settings?.hub_id || settings?.id;
  const settingsMediaQualityProfile = settings?.user_data?.media_quality_profile;

  const onStreamingSettingsChange = useCallback(
    (key, value) => {
      setSettings(prevSettings => {
        const keys = key.split(".");
        const lastKey = keys.pop();
        const newSettings = JSON.parse(JSON.stringify(prevSettings));
        const updatedSettings = keys.reduce((obj, key) => {
          if (!obj[key] || typeof obj[key] !== "object") {
            obj[key] = {};
          }
          return obj[key];
        }, newSettings);
        updatedSettings[lastKey] = value;
        return newSettings;
      });
    },
    [setSettings]
  );

  useEffect(() => {
    const dialog = APP.dialog;

    if (!dialog?.on) return undefined;

    const onDialogMediaQualityChanged = profile => {
      setRoomMediaQualityState(currentState =>
        normalizeDialogMediaQualityState(withPersistedMediaQualityProfile(profile, settings), currentState)
      );
    };

    dialog.on(DIALOG_MEDIA_QUALITY_CHANGED, onDialogMediaQualityChanged);
    setRoomMediaQualityState(currentDialogMediaQualityState(settings, roomSessionPreview));

    return () => {
      dialog.off?.(DIALOG_MEDIA_QUALITY_CHANGED, onDialogMediaQualityChanged);
    };
  }, [roomSessionPreview, settings, settingsMediaQualityProfile]);

  useEffect(() => {
    setRoomMediaQualityState(currentDialogMediaQualityState(settings, roomSessionPreview));
  }, [roomSessionPreview, settings, settingsRoomId, settingsMediaQualityProfile]);

  const onRoomMediaQualityChange = useCallback(
    (_, data) => {
      const nextProfile = data.value;

      setRoomMediaQualityState(currentState => normalizeDialogMediaQualityState(nextProfile, currentState));
      onStreamingSettingsChange("user_data.media_quality_profile", nextProfile);
    },
    [onStreamingSettingsChange]
  );

  const roomMediaQualityCapability = roomSessionMediaQualityState(settings, roomSessionPreview);
  return (
    <VerseSettings
      moderationChildren={<ModerationChildren styles={styles} settings={settings} setSettings={setSettings} />}
      resourceLimitsChildren={
        <ResourceLimitsChildren
          styles={styles}
          settings={settings}
          setSettings={setSettings}
          roomSessionLimits={roomSessionPreview?.limits}
        />
      }
      streamingChildren={
        roomMediaQualityCapability?.canChange ? (
          <StreamingChildren
            styles={styles}
            roomMediaQualityState={roomMediaQualityState}
            roomMediaQualityCapability={roomMediaQualityCapability}
            onRoomMediaQualityChange={onRoomMediaQualityChange}
          />
        ) : null
      }
      accessChildren={
        <AccessChildren
          styles={styles}
          settings={settings}
          setSettings={setSettings}
          fetchingInvite={fetchingInvite}
          inviteUrl={inviteUrl}
          revokeInvite={revokeInvite}
        />
      }
      advancedChildren={<AdvancedChildren styles={styles} settings={settings} setSettings={setSettings} />}
    ></VerseSettings>
  );
}

VerseSettingsContainer.propTypes = {
  hubChannel: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
  settings: PropTypes.object.isRequired,
  setSettings: PropTypes.func.isRequired,
  roomSession: PropTypes.object
};

function mediaQualityLabel(profile) {
  if (profile === "high") {
    return <FormattedMessage id="room-settings-sidebar.media-quality-high" defaultMessage="High" />;
  }
  if (profile === "standard") {
    return <FormattedMessage id="room-settings-sidebar.media-quality-standard" defaultMessage="Standard" />;
  }

  return <FormattedMessage id="room-settings-sidebar.media-quality-low" defaultMessage="Low" />;
}

function StreamingChildren({ styles, roomMediaQualityState, roomMediaQualityCapability, onRoomMediaQualityChange }) {
  const classes = useClasses();
  const allowedProfiles = roomMediaQualityCapability?.allowedProfiles || [];

  if (!roomMediaQualityCapability?.canChange) {
    return null;
  }

  return (
    <div className={mergeClasses(styles.row, classes.mediaQualityRow)}>
      <Label>
        <FormattedMessage id="room-settings-sidebar.media-quality" defaultMessage="Media quality" />
      </Label>
      <RadioGroup
        layout="horizontal"
        className={classes.mediaQualityInputs}
        value={roomMediaQualityState.profile}
        onChange={onRoomMediaQualityChange}
      >
        {allowedProfiles.map(profile => (
          <Radio key={profile} value={profile} label={mediaQualityLabel(profile)} />
        ))}
      </RadioGroup>
    </div>
  );
}

StreamingChildren.propTypes = {
  styles: PropTypes.object,
  roomMediaQualityState: PropTypes.object.isRequired,
  roomMediaQualityCapability: PropTypes.object.isRequired,
  onRoomMediaQualityChange: PropTypes.func.isRequired
};

function ModerationChildren({ styles, settings, setSettings }) {
  const onSettingsChange = useCallback(
    (key, value) => {
      setSettings(prevSettings => {
        const keys = key.split(".");
        const lastKey = keys.pop();
        // Deep clone the settings object to avoid mutation
        const newSettings = JSON.parse(JSON.stringify(prevSettings));
        const updatedSettings = keys.reduce((obj, key) => {
          if (!obj[key] || typeof obj[key] !== "object") {
            obj[key] = {};
          }
          return obj[key];
        }, newSettings);
        updatedSettings[lastKey] = value;
        return newSettings;
      });
    },
    [setSettings]
  );

  useEffect(() => {
    if (!settings.member_permissions.spawn_and_move_media) {
      if (settings.member_permissions.spawn_camera !== false) {
        onSettingsChange("member_permissions.spawn_camera", false);
      }
      if (settings.member_permissions.pin_objects !== false) {
        onSettingsChange("member_permissions.pin_objects", false);
      }
    }
  }, [
    settings.member_permissions.spawn_and_move_media,
    onSettingsChange,
    settings.member_permissions.spawn_camera,
    settings.member_permissions.pin_objects
  ]);

  return (
    <>
      <div className={styles.row}>
        <VoiceChatEnabledSwitch
          voiceChatEnabled={settings.member_permissions.voice_chat}
          onVoiceChatEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.voice_chat", data.checked);
          }}
        />
      </div>
      <div className={styles.row}>
        <TextChatEnabledSwitch
          textChatEnabled={settings.member_permissions.text_chat}
          onTextChatEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.text_chat", data.checked);
          }}
        />
      </div>

      <div className={styles.row}>
        <CreateAndMoveObjectsEnabledSwitch
          createAndMoveObjectsEnabled={settings.member_permissions.spawn_and_move_media}
          onCreateAndMoveObjectsEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.spawn_and_move_media", data.checked);
          }}
        />
      </div>

      <div className={styles.row}>
        <CreateCamerasEnabledSwitch
          createCamerasEnabled={settings.member_permissions.spawn_camera}
          disabled={!settings.member_permissions.spawn_and_move_media}
          onCreateCamerasEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.spawn_camera", data.checked);
          }}
        />
      </div>

      <div className={styles.row}>
        <PinObjectsEnabledSwitch
          pinObjectsEnabled={settings.member_permissions.pin_objects}
          onPinObjectsEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.pin_objects", data.checked);
          }}
          disabled={!settings.member_permissions.spawn_and_move_media}
        />
      </div>

      <div className={styles.row}>
        <CreateDrawingsEnabledSwitch
          createDrawingsEnabled={settings.member_permissions.spawn_drawing}
          onCreateDrawingsEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.spawn_drawing", data.checked);
          }}
        />
      </div>

      <div className={styles.row}>
        <CreateEmojisEnabledSwitch
          createEmojisEnabled={settings.member_permissions.spawn_emoji}
          onCreateEmojisEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.spawn_emoji", data.checked);
          }}
        />
      </div>

      <div className={styles.row}>
        <AllowFlyingEnabledSwitch
          allowFlyingEnabled={settings.member_permissions.fly}
          onAllowFlyingEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.fly", data.checked);
          }}
        />
      </div>
    </>
  );
}

ModerationChildren.propTypes = {
  settings: PropTypes.object.isRequired,
  setSettings: PropTypes.func.isRequired,
  styles: PropTypes.object
};

function ResourceLimitsChildren({ styles, settings, setSettings, roomSessionLimits }) {
  const classes = useClasses();
  const intl = useIntl();
  const resourceAvailability = roomResourceSettingsAvailability(configManager.config, settings);
  const ccuLimitId = useId("ccu_limit");
  const microphoneLimitId = useId("microphone_limit");
  const cameraLimitId = useId("camera_limit");
  const screenShareLimitId = useId("screen_share_limit");
  const maxRoomSize = configs.feature("max_room_size");

  const onResourceLimitChange = useCallback(
    (limitKey, value) => {
      setSettings(prevSettings => nextRoomResourceLimitSettings(prevSettings, limitKey, value, roomSessionLimits));
    },
    [roomSessionLimits, setSettings]
  );

  const onParticipantLimitChange = useCallback(
    value => {
      setSettings(prevSettings =>
        nextRoomParticipantLimitSettings(prevSettings, value, maxRoomSize, roomSessionLimits)
      );
    },
    [maxRoomSize, roomSessionLimits, setSettings]
  );

  return (
    <>
      <div className={mergeClasses(styles.row, classes.wrapRow)}>
        <InfoLabel
          htmlFor={ccuLimitId}
          info={intl.formatMessage({
            id: "room-settings-sidebar.participant-limit-info",
            defaultMessage:
              "Maximum number of people who can be in this verse at the same time. Your subscription may limit this value."
          })}
        >
          <FormattedMessage id="room-settings-sidebar.participant-limit" defaultMessage="Participant limit" />
        </InfoLabel>
        <Input
          id={ccuLimitId}
          value={settings.room_size}
          onChange={e => onParticipantLimitChange(e.target.value)}
          type="number"
          min={0}
          max={roomParticipantLimitMax(maxRoomSize, roomSessionLimits)}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.participant-limit-placeholder",
            defaultMessage: "Participants"
          })}
          appearance="filled-darker"
          className={classes.numberInput}
        />
      </div>
      {resourceAvailability.maxMicrophones.visible && (
        <div className={mergeClasses(styles.row, classes.wrapRow)}>
          <InfoLabel
            htmlFor={microphoneLimitId}
            info={intl.formatMessage({
              id: "room-settings-sidebar.microphone-limit-info",
              defaultMessage:
                "Maximum number of participants who can use microphones at the same time. Your subscription may limit this value."
            })}
          >
            <FormattedMessage id="room-settings-sidebar.microphone-limit" defaultMessage="Microphone limit" />
          </InfoLabel>
          <Input
            id={microphoneLimitId}
            value={roomResourceSettingValue(settings, roomSessionLimits, "maxMicrophones")}
            onChange={e => onResourceLimitChange("maxMicrophones", e.target.value)}
            type="number"
            min={0}
            max={roomResourceSettingMax(roomSessionLimits, "maxMicrophones")}
            disabled={resourceAvailability.maxMicrophones.disabled}
            placeholder={intl.formatMessage({
              id: "room-settings-sidebar.microphone-limit-placeholder",
              defaultMessage: "Microphones"
            })}
            appearance="filled-darker"
            className={classes.numberInput}
          />
        </div>
      )}
      {resourceAvailability.maxCameras.visible && (
        <div className={mergeClasses(styles.row, classes.wrapRow)}>
          <InfoLabel
            htmlFor={cameraLimitId}
            info={intl.formatMessage({
              id: "room-settings-sidebar.camera-limit-info",
              defaultMessage:
                "Maximum number of participants who can use cameras at the same time. Your subscription may limit this value."
            })}
          >
            <FormattedMessage id="room-settings-sidebar.camera-limit" defaultMessage="Camera limit" />
          </InfoLabel>
          <Input
            id={cameraLimitId}
            value={roomResourceSettingValue(settings, roomSessionLimits, "maxCameras")}
            onChange={e => onResourceLimitChange("maxCameras", e.target.value)}
            type="number"
            min={0}
            max={roomResourceSettingMax(roomSessionLimits, "maxCameras")}
            disabled={resourceAvailability.maxCameras.disabled}
            placeholder={intl.formatMessage({
              id: "room-settings-sidebar.camera-limit-placeholder",
              defaultMessage: "Cameras"
            })}
            appearance="filled-darker"
            className={classes.numberInput}
          />
        </div>
      )}
      {resourceAvailability.maxScreenShares.visible && (
        <div className={mergeClasses(styles.row, classes.wrapRow)}>
          <InfoLabel
            htmlFor={screenShareLimitId}
            info={intl.formatMessage({
              id: "room-settings-sidebar.screen-share-limit-info",
              defaultMessage:
                "Maximum number of participants who can share their screen at the same time. Your subscription may limit this value."
            })}
          >
            <FormattedMessage id="room-settings-sidebar.screen-share-limit" defaultMessage="Screen share limit" />
          </InfoLabel>
          <Input
            id={screenShareLimitId}
            value={roomResourceSettingValue(settings, roomSessionLimits, "maxScreenShares")}
            onChange={e => onResourceLimitChange("maxScreenShares", e.target.value)}
            type="number"
            min={0}
            max={roomResourceSettingMax(roomSessionLimits, "maxScreenShares")}
            disabled={resourceAvailability.maxScreenShares.disabled}
            placeholder={intl.formatMessage({
              id: "room-settings-sidebar.screen-share-limit-placeholder",
              defaultMessage: "Screen shares"
            })}
            appearance="filled-darker"
            className={classes.numberInput}
          />
        </div>
      )}
    </>
  );
}

ResourceLimitsChildren.propTypes = {
  settings: PropTypes.object.isRequired,
  setSettings: PropTypes.func.isRequired,
  styles: PropTypes.object,
  roomSessionLimits: PropTypes.object
};

function AccessChildren({ styles, settings, setSettings, fetchingInvite, inviteUrl, revokeInvite }) {
  const onSettingsChange = useCallback(
    (key, value) => {
      setSettings(prevSettings => {
        const keys = key.split(".");
        const lastKey = keys.pop();
        // Deep clone the settings object to avoid mutation
        const newSettings = JSON.parse(JSON.stringify(prevSettings));
        const updatedSettings = keys.reduce((obj, key) => obj[key], newSettings);
        updatedSettings[lastKey] = value;

        return newSettings;
      });
    },
    [setSettings]
  );

  const sharedLinkId = useId("shared_link");
  const inviteOnlyId = useId("invite_only");
  const revocableLinkId = useId("revocable_link");

  return (
    <>
      <div className={styles.row}>
        <Label htmlFor={sharedLinkId}>
          <FormattedMessage
            id="room-settings-sidebar.access-shared-link"
            defaultMessage="Shared link (Only those with the link can join)"
          />
        </Label>
        <Radio
          id={sharedLinkId}
          value="allow"
          checked={settings.entry_mode === "allow"}
          onChange={e => onSettingsChange("entry_mode", e.target.value)}
        ></Radio>
      </div>

      <div className={styles.row}>
        <Label htmlFor={inviteOnlyId}>
          <FormattedMessage
            id="room-settings-sidebar.access-invite"
            defaultMessage="Trustworthy (Only people with a revocable link can join)"
          />
        </Label>
        <Radio
          id={inviteOnlyId}
          value="invite"
          checked={settings.entry_mode === "invite"}
          onChange={e => onSettingsChange("entry_mode", e.target.value)}
        ></Radio>
      </div>
      {settings.entry_mode === "invite" && (
        <div className={styles.row}>
          <Label htmlFor={revocableLinkId}>
            <FormattedMessage id="invite-link-input-field.label" defaultMessage="Invite link" />
          </Label>
          <InviteLinkInputField fetchingInvite={fetchingInvite} inviteUrl={inviteUrl} onRevokeInvite={revokeInvite} />
        </div>
      )}
    </>
  );
}

AccessChildren.propTypes = {
  settings: PropTypes.object.isRequired,
  setSettings: PropTypes.func.isRequired,
  styles: PropTypes.object,
  fetchingInvite: PropTypes.bool,
  inviteUrl: PropTypes.string,
  revokeInvite: PropTypes.func.isRequired
};

function AdvancedChildren({ styles, settings, setSettings }) {
  const onSettingsChange = useCallback(
    (key, value) => {
      setSettings(prevSettings => {
        const keys = key.split(".");
        const lastKey = keys.pop();
        // Deep clone the settings object to avoid mutation
        const newSettings = JSON.parse(JSON.stringify(prevSettings));
        const updatedSettings = keys.reduce((obj, key) => {
          if (!obj[key]) obj[key] = {};
          return obj[key];
        }, newSettings);

        updatedSettings[lastKey] = value;

        return newSettings;
      });
    },
    [setSettings]
  );

  const intl = useIntl();
  const classes = useClasses();

  const [portals, setPortals] = useState([...(settings.user_data?.portals ?? []), ""]);

  useEffect(() => {
    const filteredPortals = portals.filter(portal => portal !== "");
    const currentPortals = settings.user_data?.portals ?? [];

    // Only update if the filtered portals actually changed
    if (JSON.stringify(filteredPortals) !== JSON.stringify(currentPortals)) {
      onSettingsChange("user_data.portals", filteredPortals);
    }

    // Add empty portal input if all are filled
    if (portals.every(portal => portal !== "") && portals.length > 0) {
      setPortals([...portals, ""]);
    }

    // Remove extra empty portals (keep only one empty at the end)
    const emptyCount = portals.filter(portal => portal === "").length;
    if (emptyCount > 1) {
      setPortals(portals.filter(portal => portal !== ""));
    }
  }, [portals, onSettingsChange, settings.user_data?.portals]);

  const handleFovChange = useCallback(
    value => {
      let fov = value;
      if (value !== null && value !== "") {
        if (value < 30) {
          fov = 30;
        } else if (value > 160) {
          fov = 160;
        }
      }
      onSettingsChange("user_data.fov", fov);
    },
    [onSettingsChange]
  );

  return (
    <>
      <div className={styles.row}>
        <Label htmlFor="user_data.leave_room_url">
          <FormattedMessage id="room-settings-sidebar.leave-room-url" defaultMessage="Leave Verse URL" />
        </Label>
        <Input
          id="user_data.leave_room_url"
          value={settings.user_data?.leave_room_url || ""}
          onChange={e => onSettingsChange("user_data.leave_room_url", e.target.value)}
          minLength={10}
          maxLength={255}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.leave-room-url-placeholder",
            defaultMessage: "Destination URL"
          })}
          appearance="filled-darker"
          className={classes.input}
        />
      </div>

      <div className={styles.row}>
        <Label htmlFor="user_data.redirect_url">
          <FormattedMessage
            id="room-settings-sidebar.redirect-url"
            defaultMessage="URL to Redirect Users To (This not apply to Verse hosts)"
          />
        </Label>
        <Input
          id="user_data.redirect_url"
          value={settings.user_data?.redirect_url || ""}
          onChange={e => onSettingsChange("user_data.redirect_url", e.target.value)}
          minLength={10}
          maxLength={255}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.redirect-url-placeholder",
            defaultMessage: "Enter a URL"
          })}
          appearance="filled-darker"
          className={classes.input}
        />
      </div>

      <div className={styles.row}>
        <Label htmlFor="user_data.fov">
          <FormattedMessage id="room-settings-sidebar.custom-camera-fov" defaultMessage="Custom Camera Field of View" />
        </Label>
        <NumericInput
          value={settings.user_data?.fov || ""}
          onChange={handleFovChange}
          min={30}
          max={160}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.custom-camera-fov-placeholder",
            defaultMessage: "FOV"
          })}
          appearance="filled-darker"
          className={classes.input}
        />
      </div>

      <div className={styles.row}>
        <Is360TourEnabledSwitch
          is360TourEnabled={settings.user_data?.is_360_tour}
          onIs360TourEnabledChange={e => onSettingsChange("user_data.is_360_tour", e.target.checked)}
        />
      </div>

      <div className={styles.row}>
        <TourPortalsEnabledSwitch
          isTourPortalsEnabled={settings.user_data?.enable_tour_portals}
          onTourPortalsEnabledChange={e => onSettingsChange("user_data.enable_tour_portals", e.target.checked)}
        />
      </div>

      {portals.map((_, i) => (
        <div className={styles.row} key={`portal-input-${i}`}>
          {i === 0 ? (
            <Label htmlFor={`user_data.portals[${i}]`}>
              <FormattedMessage id="room-settings-sidebar.portal" defaultMessage="Portal URL" />
            </Label>
          ) : (
            <Label htmlFor={`user_data.portals[${i}]`} className={classes.childInput}>
              <FormattedMessage
                id="room-settings-sidebar.add-another-portal-placeholder"
                defaultMessage="Add another portal"
              />
            </Label>
          )}
          <Input
            id={`user_data.portals[${i}]`}
            value={portals[i] || ""}
            onChange={e => setPortals([...portals.slice(0, i), e.target.value, ...portals.slice(i + 1)])}
            minLength={10}
            maxLength={255}
            placeholder={intl.formatMessage({
              id: "room-settings-sidebar.portal-placeholder",
              defaultMessage: "Enter URL"
            })}
            appearance="filled-darker"
            className={classes.input}
          />
        </div>
      ))}

      <div className={classes.hidden}>
        <Label htmlFor="user_data.ticket_id">
          <FormattedMessage id="room-settings-sidebar.ticket_id-label" defaultMessage="Limit access by NFT Ticket ID" />
        </Label>
        <Input
          id="user_data.ticket_id"
          value={settings.user_data?.ticket_id || ""}
          onChange={e => onSettingsChange("user_data.ticket_id", e.target.value)}
          type="text"
          minLength={1}
          maxLength={64}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.ticket_id-placeholder",
            defaultMessage: "Enter NFT Ticket ID"
          })}
          appearance="filled-darker"
          className={classes.input}
        />
      </div>

      <div className={classes.hidden}>
        <Label htmlFor="user_data.event_slug">
          <FormattedMessage id="room-settings-sidebar.event-slug-label" defaultMessage="Connect room to activation" />
        </Label>
        <Input
          id="user_data.event_slug"
          value={settings.user_data?.event_slug || ""}
          onChange={e => onSettingsChange("user_data.event_slug", e.target.value)}
          type="text"
          minLength={1}
          maxLength={64}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.event-slug-placeholder",
            defaultMessage: "Enter the activation slug"
          })}
          appearance="filled-darker"
          className={classes.input}
        />
      </div>

      <div className={classes.hidden}>
        <Label htmlFor="user_data.start_time">
          <FormattedMessage
            id="room-settings-sidebar.start-time-placeholder"
            defaultMessage="Set a start time for this room"
          />
        </Label>
        <Input
          id="user_data.start_time"
          value={settings.user_data?.start_time || ""}
          onChange={e => onSettingsChange("user_data.start_time", e.target.value)}
          type="hidden"
          minLength={1}
          maxLength={64}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.start-time-placeholder",
            defaultMessage: "Enter the start time"
          })}
          className={classes.input}
        />
      </div>

      <div className={classes.hidden}>
        <Label htmlFor="user_data.end_time">
          <FormattedMessage
            id="room-settings-sidebar.end-time-placeholder"
            defaultMessage="Set an end time for this room"
          />
        </Label>
        <Input
          id="user_data.end_time"
          value={settings.user_data?.end_time || ""}
          onChange={e => onSettingsChange("user_data.end_time", e.target.value)}
          type="hidden"
          minLength={1}
          maxLength={64}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.end-time-placeholder",
            defaultMessage: "Enter the end time"
          })}
          className={classes.input}
        />
      </div>

      <div className={classes.hidden}>
        <Label htmlFor="user_data.custom_host_url">
          <FormattedMessage id="room-settings-sidebar.custom-host-url" defaultMessage="Custom Host URL" />
        </Label>
        <Input
          id="user_data.custom_host_url"
          value={settings.user_data?.custom_host_url || ""}
          onChange={e => onSettingsChange("user_data.custom_host_url", e.target.value)}
          type="hidden"
          minLength={10}
          maxLength={255}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.custom-host-url-placeholder",
            defaultMessage: "Enter a Custom Host URL"
          })}
          className={classes.input}
        />

        <Label htmlFor="user_data.subdomain">
          <FormattedMessage id="room-settings-sidebar.subdomain" defaultMessage="Subdomain" />
        </Label>
        <Input
          id="user_data.subdomain"
          value={settings.user_data?.subdomain || ""}
          onChange={e => onSettingsChange("user_data.subdomain", e.target.value)}
          type="hidden"
          minLength={10}
          maxLength={255}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.subdomain-placeholder",
            defaultMessage: "Enter a Subdomain"
          })}
          className={classes.input}
        />
      </div>
    </>
  );
}

AdvancedChildren.propTypes = {
  settings: PropTypes.object.isRequired,
  setSettings: PropTypes.func.isRequired,
  styles: PropTypes.object
};
